import type {InstagramAudioTrack} from '@Components/social-media-wizard/social-media-wizard.types';
import type {InstagramAudioSearchCacheEntry} from './instagram-audio-search.library.types';
import {INSTAGRAM_AUDIO_SEARCH_CACHE_TTL_MS} from './instagram-audio-search.library.types';

const instagramAudioSearchCache = new Map<string, InstagramAudioSearchCacheEntry>();

export const searchInstagramAudio = (instagramAccountId: number, searchQuery: string): Promise<InstagramAudioTrack[]> => {
  const cacheKey = getCacheKey(instagramAccountId, searchQuery);
  const cachedSearch = getFreshCacheEntry(cacheKey);

  if (cachedSearch) {
    return cachedSearch.request;
  }

  return cacheSearchRequest(cacheKey, fetchInstagramAudio(instagramAccountId, searchQuery));
};

export const getCachedInstagramAudioTracks = (instagramAccountId: number, searchQuery: string): InstagramAudioTrack[] | null => {
  return getFreshCacheEntry(getCacheKey(instagramAccountId, searchQuery))?.tracks ?? null;
};

const fetchInstagramAudio = async (instagramAccountId: number, searchQuery: string): Promise<InstagramAudioTrack[]> => {
  return (await window.PMW.readLocal('/socialmedia/searchInstagramAudio', {instagramAccountId, searchQuery})) as InstagramAudioTrack[];
};

const cacheSearchRequest = (cacheKey: string, request: Promise<InstagramAudioTrack[]>): Promise<InstagramAudioTrack[]> => {
  const cacheEntry: InstagramAudioSearchCacheEntry = {request, tracks: null, cachedAt: Date.now()};

  deleteExpiredCacheEntries();
  instagramAudioSearchCache.set(cacheKey, cacheEntry);
  void request.then(
    (tracks: InstagramAudioTrack[]): void => {
      cacheEntry.tracks = tracks;
    },
    (): void => {
      instagramAudioSearchCache.delete(cacheKey);
    }
  );

  return request;
};

const getFreshCacheEntry = (cacheKey: string): InstagramAudioSearchCacheEntry | null => {
  const cacheEntry = instagramAudioSearchCache.get(cacheKey);

  if (!cacheEntry || isExpired(cacheEntry)) {
    return null;
  }

  return cacheEntry;
};

const deleteExpiredCacheEntries = (): void => {
  instagramAudioSearchCache.forEach((cacheEntry: InstagramAudioSearchCacheEntry, cacheKey: string): void => {
    if (isExpired(cacheEntry)) {
      instagramAudioSearchCache.delete(cacheKey);
    }
  });
};

const isExpired = (cacheEntry: InstagramAudioSearchCacheEntry): boolean => {
  return Date.now() - cacheEntry.cachedAt > INSTAGRAM_AUDIO_SEARCH_CACHE_TTL_MS;
};

const getCacheKey = (instagramAccountId: number, searchQuery: string): string => {
  return `${instagramAccountId}:${searchQuery.trim().toLowerCase()}`;
};
